'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { getToken, loginUser, logoutUser } from '../services/auth';
import { jwtDecode } from 'jwt-decode';
import { useRouter, usePathname } from 'next/navigation';
import { apiCall } from '../helper/common_helper';
import { useUserStore } from '../store/userStore';

interface DecodedToken {
  userId: string;
  email: string;
  role: string;
  exp: number;
  type?: string;
}

interface User {
  userId: string;
  email: string;
  role: string;
  isTeamMember?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (credentials: any) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const store = useUserStore(state => state);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const path = usePathname();

  // Check if user access admin pages OR admin access user pages
  useEffect(() => {
    const token = getToken();
    if (token) {
        const decoded = jwtDecode<DecodedToken>(token);
        if (decoded.role === 'user'){
          if(path.includes('admin')){
            router.push('/dashboard');
          }
        }else{
          if(!path.includes('admin')){
            if(path.includes('/editor/')) return;
            router.push('/admin/dashboard');
          }
        }
    }
  }, [path, router]);

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token);
        if (decoded && Date.now() < decoded.exp * 1000) {
          //console.log('still login', decoded);
          setIsAuthenticated(true);
          setUser({
            userId: decoded.userId,
            email: decoded.email,
            role: decoded.role,
            isTeamMember: decoded.type === 'teamMember' ? true : false,
          });
          
        } else {
          logoutUser();
        }
      } catch (err) {
        console.error('Invalid token', err);
        logoutUser();
      }
    }
    setLoading(false); // Auth check is done
  }, []);

  useEffect(() => {
    if(isAuthenticated){
      getUserDetails();
    }
  }, [isAuthenticated]);
  

  const login = async (credentials: any) => {
    try {
      const userData = await loginUser(credentials);
      if (!userData) return;
      const decoded = jwtDecode<DecodedToken>(userData.accessToken);
      setIsAuthenticated(true);
      setUser({
        userId: decoded.userId,
        email: decoded.email,
        role: decoded.role,
        isTeamMember: decoded.type === 'teamMember' ? true : false,
      });
      if (decoded.role === 'admin') {
        router.push('/admin/users');
      } else {
        router.push('/dashboard');
      }
    } catch (err) {
      throw err;
    }
  };

  const getUserDetails = async () => {
    const res = await apiCall('GET', 'website/getDetails');
    if(res){
      if(res.status == 200){
        store.setUser(res.data);
      }
    }
  };

  const logout = async () => {
    await logoutUser();
    setIsAuthenticated(false);
    setUser(null);
    store.resetUser();
    router.push('/login');
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, login, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): any => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
